import { App, Button, Flex, Form, Select } from "@/atoms"
import { useUser } from "@/hooks/useUser"
import { PlayCircleOutlined } from "@/icons"
import { InformationAlert } from "@/molecules/InformationAlert"
import {
  useActionsWorkflow,
  useTestAction
} from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import { getWritePermission } from "@/pages/GovernancePage/helpers/workflows"
import { SlackChannelModal } from "./SlackChannelModal"
import { SlackMessageTemplateField } from "./SlackMessageTemplateField"

import styles from "../ActionsSectionForm.module.scss"

import type { SlackTestResult } from "@/api/useWorkflow.types"
import type { FormInstance } from "@/atoms"
import type { ActionProps } from ".."

export interface SlackMessageActionProps extends ActionProps {
  form: FormInstance
  selectPlaceholder: string
  params: { trigger_type?: string }
}

const PREFIX = "settings.automatedActions.form.fields.slackMessage"

export const SlackMessageAction = (props: SlackMessageActionProps) => {
  const { path, form, t, params, selectPlaceholder } = props
  const { message } = App.useApp()
  const { hasPermission } = useUser()
  const canWrite = !!hasPermission(getWritePermission())

  // Channels can still be added on the integration page in another tab.
  const { response, isLoading, refetch } = useActionsWorkflow(params, {
    enabled: !!params.trigger_type,
    refetchOnWindowFocus: "always"
  })
  const slackExtra = (data: typeof response) =>
    !Array.isArray(data) && data?.type === "slack_message" ? data : undefined
  const extra = slackExtra(response)
  const channels = extra?.channels || []

  // Channel names are unique per tenant, so the added channel is found by its name.
  const selectAddedChannel = async (name: string) => {
    const { data } = await refetch()
    const added = slackExtra(data)?.channels?.find(
      (channel) => channel.name === name
    )
    if (!added) return
    const field = ["actions", path, "slackChannels"]
    form.setFieldValue(field, [...(form.getFieldValue(field) || []), added.id])
  }

  const channelIds: string[] | undefined = Form.useWatch(
    ["actions", path, "slackChannels"],
    form
  )
  const template: string | undefined = Form.useWatch(
    ["actions", path, "messageTemplate"],
    form
  )
  const [{ testAction }, { isPending: isTesting }] = useTestAction()

  const onTest = async () => {
    try {
      const { data, error } = await testAction(
        { slackChannelIds: channelIds, messageTemplate: template },
        "slack_message",
        params.trigger_type || ""
      )
      if (error) {
        message.error(
          error.userMessages?.join(" ") || t(`${PREFIX}.testFailed`)
        )
        return
      }
      const { channelNames = [] } = (data as SlackTestResult | null) || {}
      message.success(
        t(`${PREFIX}.testSuccess`, { channels: channelNames.join(", ") })
      )
    } catch (error: any) {
      message.error(error?.data?.displayMessage || t(`${PREFIX}.testFailed`))
    }
  }

  return (
    <div className={styles.actionExtraFields}>
      {!isLoading && !channels.length && (
        <InformationAlert type="warning" message={t(`${PREFIX}.noChannels`)} />
      )}
      <Form.Item
        required={false}
        name={[path, "slackChannels"]}
        className={styles.endpointField}
        label={
          <Flex align="center" justify="space-between">
            <span>{selectPlaceholder}</span>
            <SlackChannelModal
              label={t(`${PREFIX}.addChannel`)}
              disabled={!canWrite}
              onAdded={selectAddedChannel}
            />
          </Flex>
        }
        rules={[{ required: true, message: t("error.requiredField") }]}
      >
        <Select
          mode="multiple"
          loading={isLoading}
          placeholder={selectPlaceholder}
          options={channels.map(({ id, name }) => ({ label: name, value: id }))}
        />
      </Form.Item>
      <SlackMessageTemplateField
        form={form}
        path={path}
        tags={extra?.dynamicTags || []}
        defaultTemplate={extra?.defaultMessageTemplate || ""}
        t={t}
      />
      <Flex>
        <Button
          size="small"
          icon={<PlayCircleOutlined />}
          loading={isTesting}
          disabled={!canWrite || !channelIds?.length || !template?.trim()}
          onClick={onTest}
        >
          {t(`${PREFIX}.test`)}
        </Button>
      </Flex>
    </div>
  )
}
